import React, { useState, useEffect } from 'react';
import Iconify from 'src/components/Iconify';
import propostasService from 'src/services/propostasService';
import DocumentPreview from './editor/DocumentPreview';
import DocumentCanvas from './editor/DocumentCanvas';
import CanvasReadOnly from './editor/canvas/CanvasReadOnly';
import { PROPOSTAS_DESIGN } from './Propostas.design';

function formatCurrency(value) {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

// Visualização somente-leitura da proposta, feita para ocupar o lugar de um
// painel já existente (ex.: a coluna de seções do ModalNegocio) em vez de abrir
// sobreposta. Nasceu porque mandar o usuário para o editor de rota inteira só
// para conferir o documento era lento e derrubava o contexto do negócio.
//
// O documento usa os mesmos componentes da pré-visualização do editor
// (DocumentPreview + CanvasReadOnly/DocumentCanvas readOnly), então o que
// aparece aqui é igual ao PDF e ao que o cliente vê.
export default function PropostaInlineView({ propostaId, onVoltar, onEditar }) {
  const [proposta, setProposta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (propostaId) carregar();
  }, [propostaId]);

  const carregar = async () => {
    try {
      setLoading(true);
      setErro(null);
      const response = await propostasService.buscar(propostaId);
      if (!response.error && response.data) {
        setProposta(response.data);
      } else {
        setErro('Proposta não encontrada.');
      }
    } catch (error) {
      console.error('Erro ao carregar proposta:', error);
      setErro('Não foi possível carregar a proposta.');
    } finally {
      setLoading(false);
    }
  };

  const statusCfg = PROPOSTAS_DESIGN.status[proposta?.status] || PROPOSTAS_DESIGN.status.rascunho;
  const modoCanvas = proposta?.modo_design === 'canvas';

  return (
    <div className="h-full flex flex-col min-h-0 animate-in fade-in slide-in-from-right-4 duration-300">
      {/* Header: voltar para as seções do negócio + identificação da proposta */}
      <div className="flex items-center gap-2.5 pb-3 mb-3 border-b border-gray-100 dark:border-[#383838] flex-shrink-0">
        <button
          type="button"
          onClick={onVoltar}
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-[#383838] hover:text-gray-800 dark:hover:text-[#D1D2D3] transition-colors flex-shrink-0"
          title="Voltar"
        >
          <Iconify icon="eva:arrow-back-outline" width={18} />
        </button>
        <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center flex-shrink-0">
          <Iconify icon="mdi:file-document-outline" width={18} className="text-[#7C3AED]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-[#D1D2D3] truncate">
              {proposta?.titulo || 'Proposta'}
            </p>
            {proposta && (
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md flex-shrink-0 ${statusCfg.bg} ${statusCfg.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                {statusCfg.label}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-[#ABABAD] truncate">
            {proposta ? formatCurrency(proposta.valor_total) : 'Carregando...'}
          </p>
        </div>
        {onEditar && (
          <button
            type="button"
            onClick={() => onEditar(proposta)}
            disabled={!proposta}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-50 text-white text-xs font-medium rounded-xl transition-all shadow-sm shadow-[#7C3AED]/20 active:transform active:scale-95 flex-shrink-0"
            title="Abrir no editor de propostas"
          >
            <Iconify icon="eva:edit-2-outline" width={14} />
            Editar
          </button>
        )}
      </div>

      {/* Documento */}
      <div className="flex-1 min-h-0 overflow-y-auto rounded-xl bg-gray-200/70 dark:bg-[#15181b] p-4">
        {loading ? (
          <div className="h-full flex flex-col items-center justify-center gap-3">
            <Iconify icon="eos-icons:loading" width={28} className="text-[#7C3AED] animate-spin" />
            <p className="text-sm text-gray-500 dark:text-[#ABABAD]">Carregando proposta...</p>
          </div>
        ) : erro ? (
          <div className="h-full flex flex-col items-center justify-center gap-3">
            <div className="p-4 rounded-full bg-red-50 dark:bg-red-500/10">
              <Iconify icon="eva:alert-triangle-outline" width={28} className="text-red-500" />
            </div>
            <p className="text-sm text-gray-500 dark:text-[#ABABAD]">{erro}</p>
          </div>
        ) : (
          <DocumentPreview>
            {modoCanvas ? (
              <CanvasReadOnly proposta={proposta} />
            ) : (
              <DocumentCanvas proposta={proposta} onChange={() => {}} readOnly isModelo={false} />
            )}
          </DocumentPreview>
        )}
      </div>
    </div>
  );
}
